import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { CodeListItem } from "Models/ICodeListDto";
import type { RefCallBack } from "react-hook-form";
import styled from "styled-components";

const StyledFormControlLabel = styled(FormControlLabel)<{
  $isSelected: boolean;
  $size: "small" | "medium";
}>`
    display: flex;
    flex-flow: row;
    align-items: flex-start;
    margin-left: ${(props) => props.theme.spacing(1)};
    margin-right: ${(props) => props.theme.spacing(5)};
  
    .MuiFormControlLabel-label {
      padding-top: 5px;
      p {
        font-weight: 500;
        font-size: ${(props) => (props.$size === "small" ? "14px" : "18px")};
      }
    }
  
    color: ${(props) => props.$isSelected && props.theme.palette.primary.main};
  `;

const StyledIcon = styled.div<{ disabled?: boolean }>`
    width: 16px;
    min-width: 16px;
    height: 16px;
    position: relative;
    border-radius: 50%;
    border: 1px solid ${(props) => props.theme.palette.text.secondary};
  `;

const StyledIconChecked = styled(StyledIcon)<{ disabled?: boolean }>`
    &::before {
      content: "";
      position: absolute;
      left: 2px;
      top: 2px;
      width: 10px;
      height: 10px;
      background-color: ${(props) =>
        props.disabled
          ? props.theme.palette.text.disabled
          : props.theme.palette.primary.light};
      border-radius: 50%;
    }
  `;

const Description = styled(Typography)`
    color: ${(props) => props.theme.palette.text.secondary};
    font-size: 12px !important;
    text-align: start;
  `;

type Props = {
  codeList: Array<CodeListItem>;
  value: string | null;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  useCodesAsLabels?: boolean;
  disabled?: boolean;
  row?: boolean;
  inputRef?: RefCallBack;
  label?: string;
  justifyContent?: "center" | "flex-start";
  size?: "small" | "medium";
};

export const RadioButtons = ({
  codeList,
  value,
  onChange,
  onBlur,
  name,
  useCodesAsLabels,
  disabled,
  row,
  inputRef,
  label,
  justifyContent = "flex-start",
  size = "medium",
}: Props) => (
  <Stack component="fieldset" sx={{ all: "unset" }} gap={1}>
    {label && (
      <Typography component="legend" fontSize={12} fontWeight={400}>
        {label}
      </Typography>
    )}
    <Box sx={{ width: "100%" }}>
      <RadioGroup
        value={value ?? ""}
        name={name}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={onBlur}
        row={row}
        className="radio-group"
        ref={inputRef}
        sx={{ width: "100%", display: "flex", justifyContent }}
      >
        {codeList.map((item) => (
          <StyledFormControlLabel
            $isSelected={item.code === value}
            $size={size}
            disabled={disabled}
            key={item.code.toString()}
            value={item.code.toString()}
            control={
              <Radio
                icon={<StyledIcon disabled={disabled} />}
                checkedIcon={<StyledIconChecked disabled={disabled} />}
                disabled={disabled}
                sx={{ p: size === "small" ? "7px" : "9px" }}
              />
            }
            label={
              <div>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography
                    textAlign={justifyContent === "center" ? "center" : "start"}
                  >
                    {useCodesAsLabels === true ? item.code : item.name}
                  </Typography>
                  {!!item.tooltip && (
                    <Tooltip title={item.tooltip} arrow>
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: "text.secondary",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                {!!item.description && (
                  <Description>{item.description}</Description>
                )}
              </div>
            }
          />
        ))}
      </RadioGroup>
    </Box>
  </Stack>
);
